import { useCallback, useEffect, useRef, useState } from "react";

import agent from "../../utils/axios";
import { useQuickBooksAuthPopup } from "../../hooks/useQuickBooksAuthPopup";

/**
 * - converted: ConvertToCheck accepted the bills.
 * - signInIncomplete: the company needed a QuickBooks sign-in that was blocked, closed or
 *   failed (no bills were touched — the API checks the token before changing anything).
 */
export type ConvertOutcome = "converted" | "signInIncomplete";

/**
 * Convert to check for Needs attention and Bill detail (one flow, as before AB#6872):
 * POST ConvertToCheck with the chosen company's RealmId using CSI Pay's stored token; when
 * the API answers `requiresAuth`, open the QuickBooks sign-in popup for THAT company
 * (the one the person picked, not the callback's realm) and retry once.
 * Errors from the API are thrown to the caller.
 */
export function useConvertToCheck() {
  const { authenticate } = useQuickBooksAuthPopup();
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const convert = useCallback(
    async (
      billRefCodes: string[],
      realmId: string
    ): Promise<ConvertOutcome> => {
      setBusy(true);
      try {
        const result = await agent.Bills.convertToCheck(billRefCodes, realmId);
        if (!result?.requiresAuth) return "converted";

        const signedIn = await authenticate(realmId);
        if (!signedIn) return "signInIncomplete";

        const retry = await agent.Bills.convertToCheck(billRefCodes, realmId);
        return retry?.requiresAuth ? "signInIncomplete" : "converted";
      } finally {
        if (mounted.current) setBusy(false);
      }
    },
    [authenticate]
  );

  return { convert, busy };
}

export default useConvertToCheck;
